import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

import './PageSidebarCalendar.scss';

interface PageSidebarCalendarProps {
  value: Dayjs;
  onSelect(value: Dayjs): void;
  className?: string;
}

const MONTHS_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const CALENDAR_CELLS = 42;

const buildCalendarGrid = (viewMonth: Dayjs): Dayjs[] => {
  const start = viewMonth.startOf('month');
  // Monday-first offset: Mon=0 .. Sun=6; dayjs day() returns 0=Sun
  const dow = start.day();
  const offset = dow === 0 ? 6 : dow - 1;
  const gridStart = start.subtract(offset, 'day');

  return Array.from({ length: CALENDAR_CELLS }, (_, index) =>
    gridStart.add(index, 'day')
  );
};

export const PageSidebarCalendar: FC<PageSidebarCalendarProps> = ({
  value,
  onSelect,
  className,
}) => {
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    value.startOf('month')
  );

  useEffect(() => {
    setViewMonth((prev) =>
      value.isSame(prev, 'month') ? prev : value.startOf('month')
    );
  }, [value]);

  const today = dayjs().startOf('day');
  const grid = buildCalendarGrid(viewMonth);

  return (
    <div className={cn('PageSidebarCalendar', className)}>
      <div className="PageSidebarCalendar__header">
        <button
          type="button"
          className="PageSidebarCalendar__nav-button"
          aria-label="Предыдущий месяц"
          onClick={() => setViewMonth(viewMonth.subtract(1, 'month'))}
        >
          <LeftOutlined />
        </button>
        <span className="PageSidebarCalendar__month">
          {MONTHS_RU[viewMonth.month()]} {viewMonth.year()}
        </span>
        <button
          type="button"
          className="PageSidebarCalendar__nav-button"
          aria-label="Следующий месяц"
          onClick={() => setViewMonth(viewMonth.add(1, 'month'))}
        >
          <RightOutlined />
        </button>
      </div>

      <div className="PageSidebarCalendar__weekdays">
        {WEEKDAYS_RU.map((weekday) => (
          <span key={weekday} className="PageSidebarCalendar__weekday">
            {weekday}
          </span>
        ))}
      </div>

      <div className="PageSidebarCalendar__grid">
        {grid.map((day) => {
          const isSelected = day.isSame(value, 'day');
          const isToday = day.isSame(today, 'day');
          const isOutside = !day.isSame(viewMonth, 'month');

          return (
            <div key={day.valueOf()} className="PageSidebarCalendar__day">
              <button
                type="button"
                className={cn('PageSidebarCalendar__day-button', {
                  'PageSidebarCalendar__day-button--selected': isSelected,
                  'PageSidebarCalendar__day-button--today': isToday,
                  'PageSidebarCalendar__day-button--outside': isOutside,
                })}
                aria-current={isSelected ? 'date' : undefined}
                onClick={() => onSelect(day)}
              >
                {day.date()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
